import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { motion, AnimatePresence } from "framer-motion";
import { useAlertStore, AlertType } from "../../../store/alertStore";
import styles from "./AlertSystem.module.scss";

const AlertSystem: React.FC = () => {
  const { alerts, removeAlert } = useAlertStore();
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (alerts.length > 0) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [alerts.length]);

  // Handle responsive positioning
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setIsMobile(width <= 768 || (width >= 1000 && height >= 1900)); // mirror mobile at >=1000px
    };

    // Check initial size
    checkScreenSize();

    // Add event listener for resize
    window.addEventListener("resize", checkScreenSize);

    // Cleanup
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const getIcon = (type: AlertType) => {
    switch (type) {
      case "success":
        return "/assets/images/svg/done.svg";
      case "error":
        return "/assets/images/svg/cross_red.svg";
      case "info":
        return "/assets/images/svg/notVerifiedIcon.svg";
      default:
        return "/assets/images/svg/notVerifiedIcon.svg";
    }
  };

  const getPositionClass = () => {
    return isMobile ? styles.top : styles.bottom;
  };

  const shouldShowIcon = (alert: any) => {
    // Only show icon on mobile and when showIcon is true
    return isMobile && alert.showIcon;
  };

  // Animation variants for sliding
  const slideVariants = {
    hidden: {
      opacity: 0,
      y: isMobile ? -50 : 50,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
    },
    exit: {
      opacity: 0,
      y: isMobile ? 50 : -50,
      scale: 0.95,
    },
  };

  if (!isVisible || alerts.length === 0) return null;

  return (
    <div className={styles.alertContainer}>
      <AnimatePresence mode="popLayout">
        {alerts.map((alert) => (
          <motion.div
            key={alert.id}
            variants={slideVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.3, ease: "easeOut" }}
            layout
            className={classNames(
              styles.alert,
              styles[alert.type],
              getPositionClass()
            )}
          >
            <div className={styles.alertContent}>
              {shouldShowIcon(alert) && (
                <img
                  src={getIcon(alert.type)}
                  alt={`${alert.type} icon`}
                  className={styles.icon}
                />
              )}
              <div className={styles.textContent}>
                <h4 className={styles.title}>{alert.title}</h4>
              </div>
              <img
                onClick={() => removeAlert(alert.id)}
                src={"/assets/images/svg/cross_icon.svg"}
                alt="Close alert"
                className={styles.closeButton}
                style={{ cursor: "pointer" }}
              />
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default AlertSystem;